import { Logger } from "@feature-hub/core";
import { SerializedStateManagerV1 } from "@feature-hub/serialized-state-manager";
import { FeatureAppMemoryStoreV1 } from "@volkswagen-onehub/feature-app-memory-store";
import { UniversalState } from "./universal-state";
import { logBffLoggerCalls } from "./log-bff-logger-calls";
import { BaseBffResponse } from "@volkswagen-onehub/vw-starter-types-support";

/**
 * Manages all interactions with the serialized state manager necessary to bring the bff response to the client.
 * Additionally the bff log events from the response are logged here.
 * Should be used on client and server before actually calling the bff.
 * The bff response must be set to the memory store after it is retrieved.
 *
 * @param memoryStore memory store caches BFF calls to improve feature app start up time
 * @param logger used to log BFF errors
 * @param serializedStateManager pass this if SSR is enabled for feature app
 */
export function manageBffSerializationAndCaching<BffResponse extends BaseBffResponse>(
  serializedStateManager: SerializedStateManagerV1,
  memoryStore: FeatureAppMemoryStoreV1<UniversalState<BffResponse>>,
  logger: Logger
) {
  // retrieve serialized state on the client
  const serializedState = serializedStateManager?.getSerializedState();

  if (serializedState) {
    const universalState: UniversalState<BffResponse> =
      JSON.parse(serializedState);

    if (!memoryStore.getState()) {
      memoryStore.setState(universalState);
    }

    const { status } = universalState;

    const bffLoggerCalls =
      status === "loaded" ? universalState.payload.bffLoggerCalls : undefined;

    if (bffLoggerCalls) {
      logBffLoggerCalls("Starter", bffLoggerCalls, logger);
    }
  }

  // serialize the state on the server
  serializedStateManager?.register(() =>
    JSON.stringify(memoryStore.getState())
  );
}
